import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight, Paperclip, Plus, CaretDown, Trash, FileText,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";
import RFPDashboard from "@/components/erp/RFPDashboard";

const AGENT_KEY = "rfp-create";
const CHAT_ENDPOINT = "/erp-agents/rfp/chat";

/* Markdown renderer for assistant messages */
function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li: ({ node, ...props }) => <li className="text-sm" {...props} />,
          h1: ({ node, ...props }) => <h1 className="text-base font-bold mb-1 mt-2" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-sm font-bold mb-1 mt-2" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...props} />,
          code: ({ node, inline, ...props }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...props} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...props} />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table className="w-full text-xs border-collapse" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-[#0020C2]/5" {...props} />,
          th: ({ node, ...props }) => (
            <th className="border border-gray-200 px-2 py-1.5 text-left font-bold uppercase tracking-wider text-[10px] text-gray-600" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="border border-gray-200 px-2 py-1.5 text-gray-800" {...props} />
          ),
          a: ({ node, ...props }) => <a className="text-[#0020C2] underline" {...props} />,
          hr: () => <hr className="my-2 border-gray-200" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

/* Parse item / quantity / unit price from the user's chat messages */
function parseLineFromMessages(messages, fieldUi) {
  let item = fieldUi?.item || null;
  let qty = (fieldUi?.quantity != null && fieldUi.quantity !== "")
    ? Number(fieldUi.quantity) : null;
  let price = null;
  let currency = null;

  // Known ISO currency codes we recognise in free text.
  const CCY = /\b(AED|USD|EUR|GBP|SAR|QAR|KWD|BHD|OMR|INR|PKR|EGP|JPY|CNY|CAD|AUD|CHF|SGD)\b/i;
  const SYMBOL_TO_CCY = { "$": "USD", "€": "EUR", "£": "GBP", "₹": "INR", "¥": "JPY" };

  const detectCurrency = (t) => {
    const m = t.match(CCY);
    if (m) return m[1].toUpperCase();
    for (const sym of Object.keys(SYMBOL_TO_CCY)) {
      if (t.includes(sym)) return SYMBOL_TO_CCY[sym];
    }
    return null;
  };

  const isSynthetic = (t) =>
    /^set the buyer to/i.test(t) ||
    /\bcategory (confirmed|selected)/i.test(t) ||
    /\bselected supplier/i.test(t) ||
    /^set the /i.test(t) ||
    /\(id \d{5,}\)/i.test(t);

  const userMsgs = (messages || [])
    .filter((m) => m.role === "user" && (m.text || "").trim())
    .map((m) => m.text.trim())
    .filter((t) => !isSynthetic(t));

  const STOP = /\b(at|@|for|price|each|set|and|to|please|amount|is|usd|aed|eur|gbp|sar|inr)\b/i;

  const cleanItem = (w) => {
    if (!w) return null;
    let out = w.split(STOP)[0].trim();
    out = out.replace(/[.,;:]+$/, "").trim();
    if (out.length < 2) return null;
    if (out.length > 3 && /s$/i.test(out) && !/ss$/i.test(out)) out = out.replace(/s$/i, "");
    return out;
  };

  for (const text of userMsgs) {
    let mItem = null, mQty = null, mPrice = null, mCcy = null;

    mCcy = detectCurrency(text);

    // Amount patterns: "at/price/each 800", "amount is 800", "800 USD",
    // "USD 800", "$800". Prefer an amount that sits next to a currency/keyword.
    const priceKw = text.match(/(?:\bat\b|@|\bprice\b|\beach\b|\bper unit\b|\bamount\b(?:\s+is)?)\s*(?:aed|usd|eur|gbp|sar|inr|\$|€|£|₹|¥)?\s*(\d+(?:\.\d+)?)/i);
    const priceCcyAfter = text.match(/(\d+(?:\.\d+)?)\s*(?:AED|USD|EUR|GBP|SAR|QAR|KWD|INR|PKR|EGP|JPY|CNY|CAD|AUD|CHF|SGD)\b/i);
    const priceCcyBefore = text.match(/(?:AED|USD|EUR|GBP|SAR|QAR|KWD|INR|PKR|EGP|JPY|CNY|CAD|AUD|CHF|SGD)\s*(\d+(?:\.\d+)?)/i);
    const priceSymbol = text.match(/[$€£₹¥]\s*(\d+(?:\.\d+)?)/);
    if (priceKw) mPrice = parseFloat(priceKw[1]);
    else if (priceCcyAfter) mPrice = parseFloat(priceCcyAfter[1]);
    else if (priceCcyBefore) mPrice = parseFloat(priceCcyBefore[1]);
    else if (priceSymbol) mPrice = parseFloat(priceSymbol[1]);

    const pair = text.match(/\b(\d+)\s+([a-zA-Z][a-zA-Z \-]{1,40})/);
    if (pair) {
      mQty = parseFloat(pair[1]);
      mItem = cleanItem(pair[2]);
    }

    if (!mItem || mQty == null) {
      const rev = text.match(/\b([a-zA-Z][a-zA-Z \-]{1,40}?)\s+(\d+)\b/);
      if (rev) {
        if (mQty == null) mQty = parseFloat(rev[2]);
        if (!mItem) mItem = cleanItem(rev[1]);
      }
    }

    if (!mItem) {
      const im = text.match(/\b(?:for|of|procure|sourcing)\s+(?:\d+\s+)?([a-zA-Z][a-zA-Z \-]{1,40})/i);
      if (im) mItem = cleanItem(im[1]);
    }

    if (!item && mItem) item = mItem;
    if (qty == null && mQty != null) qty = mQty;
    if (price == null && mPrice != null) price = mPrice;
    if (currency == null && mCcy != null) currency = mCcy;

    if (item && qty != null && price != null) break;
  }

  return { item, qty, price, currency };
}

/* RFP line input (item + quantity + amount) -> resolve-category */
function RFPLineInput({ fieldUi, messages, done, onResolved }) {
  const buId = fieldUi?.procurement_bu_id ?? null;

  const parsed = React.useMemo(
    () => parseLineFromMessages(messages, fieldUi),
    [messages, fieldUi]
  );

  // Currency: prefer what the user typed in the message ("800 USD" -> USD),
  // then any currency the widget was seeded with, else AED.
  const defaultCurrency = parsed.currency || fieldUi?.currency || "AED";

  const [currency, setCurrency] = React.useState(defaultCurrency);
  const [currencyOptions, setCurrencyOptions] = React.useState([]);

  // If a later message reveals a currency (e.g. user adds "in USD"), adopt it.
  React.useEffect(() => {
    if (parsed.currency && parsed.currency !== currency) {
      setCurrency(parsed.currency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed.currency]);

  React.useEffect(() => {
    let alive = true;
    api.get("/erp-agents/rfp/currencies")
      .then(({ data }) => {
        if (!alive) return;
        const list = data?.currencies || [];
        setCurrencyOptions(list);
        // If the default isn't in the list, keep it anyway (Oracle accepts it).
      })
      .catch(() => { if (alive) setCurrencyOptions([]); });
    return () => { alive = false; };
  }, []);

  const itemLocked = !!parsed.item;
  const qtyLocked = parsed.qty != null;
  const priceKnown = parsed.price != null;

  const [item, setItem] = React.useState(parsed.item || "");
  const [qty, setQty] = React.useState(parsed.qty != null ? String(parsed.qty) : "");
  const [unitPrice, setUnitPrice] = React.useState(parsed.price != null ? String(parsed.price) : "");

  const [phase, setPhase] = React.useState("input");
  const [err, setErr] = React.useState("");
  const autoFired = React.useRef(false);

  const qtyNum = parseFloat(qty) || 0;
  const price = parseFloat(unitPrice) || 0;
  const lineTotal = qtyNum > 0 ? qtyNum * price : price;
  const canProceed = item.trim().length > 0 && qtyNum > 0;

  const resolveCategory = React.useCallback(async (amountGiven, itemArg, qtyArg, priceArg) => {
    const useItem = (itemArg ?? item).trim();
    const useQty = qtyArg ?? qtyNum;
    const usePrice = priceArg ?? price;
    if (!buId) { setErr("No Procurement BU id - re-select the BU in the wizard."); setPhase("error"); return; }
    if (!useItem) { setErr("Enter an item description first."); return; }
    setPhase("resolving");
    setErr("");
    try {
      const { data } = await api.post("/erp-agents/rfp/resolve-category", {
        procurement_bu_id: buId,
        item_text: useItem,
        with_suppliers: false,
        quantity: useQty ?? null,
        unit_price: usePrice || null,
        currency,
      });
      onResolved?.({
        api: data,
        item: useItem,
        quantity: useQty,
        amount: amountGiven ? (useQty > 0 ? useQty * usePrice : usePrice) : null,
        currency,
        procurement_bu_id: buId,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Category lookup failed.");
      setPhase("error");
    }
  }, [buId, item, qtyNum, price, currency, onResolved]);

  const autoResolving = itemLocked && qtyLocked && priceKnown;
  React.useEffect(() => {
    if (autoFired.current || done) return;
    if (autoResolving && parsed.item && parsed.qty != null) {
      autoFired.current = true;
      resolveCategory(true, parsed.item, parsed.qty, parsed.price);
    }
  }, [autoResolving, parsed, resolveCategory, done]);

  if (done) {
    return (
      <div className="mt-2 flex items-center gap-2 text-[11px] text-emerald-700
        bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-1.5"
        data-testid="rfp-line-input-done">
        <span className="font-bold">{"\u2713"}</span>
        <span>
          {(qty || parsed.qty || "")}{" \u00d7 "}
          {(item || parsed.item || "item")}
          {price > 0 && <> {"\u00b7"} {currency} {lineTotal.toFixed(2)}</>}
          {" submitted"}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid="rfp-line-input">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          {autoResolving ? "Processing your request"
            : (itemLocked ? "Confirm amount" : "What are you sourcing?")}
        </span>
      </div>

      {phase === "input" && !autoResolving && (
        <div className="p-3 space-y-2.5">
          {!itemLocked ? (
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Item description</label>
              <input
                autoFocus
                value={item}
                onChange={(e) => setItem(e.target.value)}
                placeholder="e.g. laptop, office chair, cement"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg
                  focus:outline-none focus:border-[#0020C2]"
              />
            </div>
          ) : (
            <div className="text-[11px] text-gray-500">Item: <span className="font-semibold text-gray-800">{item}</span></div>
          )}

          {!qtyLocked ? (
            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Quantity</label>
              <input
                autoFocus={itemLocked}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="e.g. 20"
                inputMode="decimal"
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg text-right
                  focus:outline-none focus:border-[#0020C2]"
              />
            </div>
          ) : (
            <div className="text-[11px] text-gray-500">Quantity: <span className="font-semibold text-gray-800">{qty}</span></div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
              Unit price <span className="normal-case font-normal text-gray-400">(optional - enables price analysis)</span>
            </label>
            <div className="flex items-center gap-1.5">
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="shrink-0 px-2 py-2 text-xs border border-gray-300 rounded-lg bg-white
                  focus:outline-none focus:border-[#0020C2] cursor-pointer"
                title="Currency"
              >
                {/* Ensure the current value is always selectable even if not in the list */}
                {currencyOptions.length === 0 && <option value={currency}>{currency}</option>}
                {currencyOptions.length > 0 &&
                  !currencyOptions.some((c) => c.code === currency) && (
                    <option value={currency}>{currency}</option>
                  )}
                {currencyOptions.map((c) => (
                  <option key={c.code} value={c.code}>{c.code}</option>
                ))}
              </select>
              <input
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="0.00"
                inputMode="decimal"
                className="flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg text-right
                  focus:outline-none focus:border-[#0020C2]"
              />
            </div>
          </div>

          {qtyNum > 0 && price > 0 && (
            <div className="flex justify-between text-[11px] bg-gray-50 rounded-lg px-2 py-1.5">
              <span className="text-gray-500">Line total ({qtyNum} {"\u00d7"} {currency} {price.toFixed(2)})</span>
              <span className="font-mono font-bold text-gray-900">{currency} {lineTotal.toFixed(2)}</span>
            </div>
          )}

          {err && <div className="text-[11px] text-red-600">{err}</div>}

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => resolveCategory(price > 0)}
              disabled={!canProceed}
              className="flex-1 bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
                uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
              {price > 0 ? "Submit & find category" : "Continue"}
            </button>
            <button
              onClick={() => resolveCategory(false)}
              disabled={!canProceed}
              className="flex-1 border border-gray-300 text-gray-600 py-2 rounded-lg
                text-xs font-bold uppercase tracking-wider hover:bg-gray-50 disabled:opacity-40">
              Skip amount
            </button>
          </div>
        </div>
      )}

      {(phase === "resolving" || autoResolving) && phase !== "error" && (
        <div className="px-3 py-4 text-xs text-gray-400 flex items-center gap-2">
          <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
          Finding the best purchasing category for "{(item || parsed.item || "").trim()}"...
        </div>
      )}

      {phase === "error" && (
        <div className="px-3 py-3 space-y-2">
          <div className="text-xs text-red-600">{err}</div>
          <button onClick={() => setPhase("input")}
            className="text-[11px] font-bold text-[#0020C2] hover:underline">{"\u2190"} Try again</button>
        </div>
      )}
    </div>
  );
}

/* Market value analysis — insight-card style, in-stack (Tailwind + blue theme).
   Verdict badge, one-line summary, a horizontal price-position bar (market
   low→high with a marker for the requested price), and the market drivers.
   Powered by LLM + web search. Collapsible. */
function MarketNote({ market, currency, userUnitPrice }) {
  const [open, setOpen] = React.useState(true);
  if (!market) return null;

  const verdict = market.verdict || "unknown";
  const V = {
    below: { badge: "bg-emerald-100 text-emerald-700", bar: "#10b981", label: market.verdict_label || "Below market" },
    fair:  { badge: "bg-[#0020C2]/10 text-[#0020C2]",  bar: "#0020C2", label: market.verdict_label || "Fair" },
    above: { badge: "bg-rose-100 text-rose-700",        bar: "#e11d48", label: market.verdict_label || "Above market" },
    unknown:{ badge: "bg-gray-200 text-gray-600",       bar: "#9ca3af", label: market.verdict_label || "Needs review" },
  }[verdict] || { badge: "bg-gray-200 text-gray-600", bar: "#9ca3af", label: "Needs review" };

  const pr = market.price_range;
  const drivers = Array.isArray(market.drivers) ? market.drivers : [];
  const ccy = (pr && pr.currency) || currency || "";

  // Position of the user's price within the market range (0..1), with padding
  // so the marker is visible even at the extremes.
  let markerPct = null;
  let userVsRange = null;
  if (pr && userUnitPrice != null && pr.high > pr.low) {
    const raw = (userUnitPrice - pr.low) / (pr.high - pr.low);
    markerPct = Math.max(0, Math.min(1, raw)) * 100;
    if (userUnitPrice < pr.low) userVsRange = "below";
    else if (userUnitPrice > pr.high) userVsRange = "above";
    else userVsRange = "within";
  }

  const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* header */}
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <span className="flex size-4 items-center justify-center rounded-full bg-[#0020C2]/10 shrink-0">
          <Sparkle size={9} weight="fill" className="text-[#0020C2]" />
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Market analysis</span>
        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${V.badge}`}>
          {V.label}
        </span>
        {market.sources_used && <span className="text-[9px] text-gray-400">{"\u00b7"} live web</span>}
        <CaretDown size={11} weight="bold"
          className={`ml-auto text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {/* summary */}
          {market.summary && (
            <p className="text-[12px] leading-relaxed text-gray-700">{market.summary}</p>
          )}

          {/* price position bar */}
          {pr && (
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-2.5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Market range {ccy ? `(${ccy}, ${pr.unit || "per unit"})` : ""}
                </span>
                {userUnitPrice != null && (
                  <span className="text-[10px] text-gray-500">
                    yours <span className="font-mono font-bold text-gray-800">{ccy} {fmt(userUnitPrice)}</span>
                  </span>
                )}
              </div>

              <div className="relative h-2 rounded-full bg-gradient-to-r from-emerald-200 via-[#0020C2]/25 to-rose-200">
                {/* low / high ticks */}
                <span className="absolute -top-0.5 left-0 h-3 w-px bg-gray-300" />
                <span className="absolute -top-0.5 right-0 h-3 w-px bg-gray-300" />
                {/* user marker */}
                {markerPct != null && (
                  <span
                    className="absolute -top-1 size-4 rounded-full border-2 border-white shadow"
                    style={{ left: `calc(${markerPct}% - 8px)`, background: V.bar }}
                    title={`${ccy} ${fmt(userUnitPrice)}`}
                  />
                )}
              </div>

              <div className="mt-1.5 flex items-center justify-between text-[10px] font-mono text-gray-500">
                <span>{ccy} {fmt(pr.low)}</span>
                <span>{ccy} {fmt(pr.high)}</span>
              </div>

              {userVsRange && (
                <div className="mt-1.5 text-[10px] text-gray-500">
                  {userVsRange === "below" && <>Your price is <span className="text-emerald-700 font-semibold">below</span> the typical market range.</>}
                  {userVsRange === "within" && <>Your price sits <span className="text-[#0020C2] font-semibold">within</span> the typical market range.</>}
                  {userVsRange === "above" && <>Your price is <span className="text-rose-700 font-semibold">above</span> the typical market range.</>}
                </div>
              )}
            </div>
          )}

          {/* drivers */}
          {drivers.length > 0 && (
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-2.5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                What's driving prices
              </div>
              <ul className="space-y-1">
                {drivers.map((d, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[11.5px] text-gray-700">
                    <span className="mt-1 size-1.5 rounded-full bg-[#0020C2] shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {market.notes && (
            <p className="text-[10px] text-gray-500 italic">{market.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}

/* Category + Item suggestion (one combined widget) */
function CategorySuggestion({ data, onConfirm }) {
  const api_ = data?.api || {};
  const currency = data?.currency || "AED";
  const suggested = api_.matched_category || api_.matched_category_full || null;
  const fullCatalog = Array.isArray(api_.categories) ? api_.categories : [];
  const buId = data?.procurement_bu_id ?? api_.procurement_bu_id ?? null;

  const [phase, setPhase] = React.useState("category");   // category -> item -> done

  const [selectedCat, setSelectedCat] = React.useState(
    suggested
      ? { category_id: suggested.category_id, category_name: suggested.category_name }
      : null
  );
  const [changingCat, setChangingCat] = React.useState(false);
  const [catQ, setCatQ] = React.useState("");

  const catFiltered = catQ
    ? fullCatalog.filter((c) => (c.category_name || "").toLowerCase().includes(catQ.toLowerCase()))
    : fullCatalog;

  const [itemLoading, setItemLoading] = React.useState(false);
  const [itemErr, setItemErr] = React.useState("");
  const [itemMatched, setItemMatched] = React.useState(null);
  const [itemLov, setItemLov] = React.useState([]);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [organizationId, setOrganizationId] = React.useState(null);
  const [confirmedWithItem, setConfirmedWithItem] = React.useState(false);
  const [orgOptions, setOrgOptions] = React.useState([]);   // org picker choices
  const [changingItem, setChangingItem] = React.useState(false);
  const [itemQ, setItemQ] = React.useState("");

  const itemFiltered = itemQ
    ? itemLov.filter((i) =>
        (i.item_number || "").toLowerCase().includes(itemQ.toLowerCase()) ||
        (i.description || "").toLowerCase().includes(itemQ.toLowerCase()))
    : itemLov;

  // Fetch items for a specific org (used after the org is known/picked).
  const loadItemsForOrg = async (orgId) => {
    setPhase("item");
    setItemLoading(true);
    setItemErr("");
    try {
      const { data: res } = await api.post("/erp-agents/rfp/resolve-item", {
        procurement_bu_id: buId,
        category_id: selectedCat.category_id,
        item_text: data.item || "",
        organization_id: orgId,
      });
      const matched = res?.matched_item || null;
      const lov = Array.isArray(res?.items) ? res.items : [];
      setItemMatched(matched);
      setItemLov(lov);
      if (res?.organization_id != null) setOrganizationId(res.organization_id);
      if (matched) {
        setSelectedItem({
          item_id: matched.item_id,
          item_number: matched.item_number,
          description: matched.description,
        });
      }
    } catch (e) {
      setItemErr(e?.response?.data?.detail || e.message || "Item lookup failed.");
    } finally {
      setItemLoading(false);
    }
  };

  const confirmCategory = async () => {
    if (!selectedCat) return;
    setPhase("item");
    setItemLoading(true);
    setItemErr("");
    try {
      const { data: res } = await api.post("/erp-agents/rfp/resolve-item", {
        procurement_bu_id: buId,
        category_id: selectedCat.category_id,
        item_text: data.item || "",
      });

      // Multiple orgs -> ask the user to pick one (no auto choice).
      if (res?.needs_org) {
        setOrgOptions(Array.isArray(res.organizations) ? res.organizations : []);
        setItemLoading(false);
        setPhase("org");
        return;
      }

      const matched = res?.matched_item || null;
      const lov = Array.isArray(res?.items) ? res.items : [];
      setItemMatched(matched);
      setItemLov(lov);
      if (res?.organization_id != null) setOrganizationId(res.organization_id);
      if (matched) {
        setSelectedItem({
          item_id: matched.item_id,
          item_number: matched.item_number,
          description: matched.description,
        });
      }
    } catch (e) {
      setItemErr(e?.response?.data?.detail || e.message || "Item lookup failed.");
      setItemLoading(false);
    } finally {
      // Note: when needs_org we already set loading false + returned above.
      if (phase !== "org") setItemLoading(false);
    }
  };

  const pickOrg = (org) => {
    setOrganizationId(org.organization_id);
    loadItemsForOrg(org.organization_id);
  };

  const confirmAll = (skipItem = false) => {
    setPhase("done");
    const useItem = skipItem ? null : selectedItem;
    setConfirmedWithItem(!!useItem);
    const amtNote = data?.amount != null
      ? ` Line amount ${currency} ${data.amount.toFixed(2)}.`
      : " No amount provided.";
    const catNote = selectedCat
      ? `Category: ${selectedCat.category_name} (id ${selectedCat.category_id}).`
      : `No category selected for "${data.item}".`;
    const itemNote = useItem
      ? ` Item: ${useItem.item_number || useItem.description || useItem.item_id}.`
      : " No specific item — category-only line.";
    onConfirm?.({
      item: data.item,
      quantity: data.quantity,
      amount: data.amount ?? null,
      category_id: selectedCat ? selectedCat.category_id : null,
      category_name: selectedCat ? selectedCat.category_name : null,
      item_id: useItem ? useItem.item_id : null,
      item_number: useItem ? useItem.item_number : null,
      item_description: useItem ? useItem.description : null,
      organization_id: organizationId,
      currency,
      text: `${data.item} \u00d7 ${data.quantity}. ${catNote}${itemNote}${amtNote}`,
    });
  };

  const catDone = phase === "org" || phase === "item" || phase === "done";

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid="rfp-category-suggestion">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          {phase === "category" ? "Suggested category"
            : phase === "org" ? "Select organization"
            : "Category & item"}
        </span>
      </div>

      <div className="p-3 space-y-2.5">
        <div className="text-[11px] text-gray-500">
          {data.quantity} {"\u00d7"} {data.item}
          {data.amount != null && (
            <> {"\u00b7"} <span className="font-mono">{currency} {data.amount.toFixed(2)}</span></>
          )}
        </div>

        {/* MARKET VALUE ANALYSIS note (LLM + web search) */}
        {api_.market && (
          <MarketNote
            market={api_.market}
            currency={currency}
            userUnitPrice={
              data.amount != null && data.quantity
                ? data.amount / data.quantity
                : (data.amount != null ? data.amount : null)
            }
          />
        )}

        {/* CATEGORY block */}
        <div className="space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Category</div>
          {selectedCat ? (
            <div className="rounded-lg border border-[#0020C2]/20 bg-[#0020C2]/5 px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-bold text-gray-900 truncate">{selectedCat.category_name}</div>
                  <div className="text-[10px] text-gray-500 font-mono">id {selectedCat.category_id}</div>
                </div>
                {!catDone && (
                  <button
                    onClick={() => setChangingCat((v) => !v)}
                    className="shrink-0 text-[10px] font-bold uppercase tracking-wider
                      text-[#0020C2] hover:underline flex items-center gap-0.5">
                    Change <CaretDown size={10} weight="bold" className={changingCat ? "rotate-180" : ""} />
                  </button>
                )}
              </div>
              {suggested && selectedCat.category_id === suggested.category_id && suggested.reason && (
                <div className="text-[11px] text-gray-500 mt-1">{suggested.reason}</div>
              )}
              {suggested && selectedCat.category_id === suggested.category_id && suggested.confidence != null && (
                <div className="text-[10px] text-gray-400 mt-0.5">
                  confidence {Math.round(suggested.confidence * 100)}%
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500">
              No category matched automatically - pick one below.
            </div>
          )}

          {!catDone && (changingCat || !selectedCat) && (
            <div className="border border-[#0020C2]/20 rounded-lg overflow-hidden">
              <input
                autoFocus
                value={catQ}
                onChange={(e) => setCatQ(e.target.value)}
                placeholder="Search all categories..."
                className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
              />
              <div className="max-h-40 overflow-y-auto">
                {fullCatalog.length === 0 && (
                  <div className="px-3 py-2 text-[11px] text-gray-400">No catalog categories available.</div>
                )}
                {fullCatalog.length > 0 && catFiltered.length === 0 && (
                  <div className="px-3 py-2 text-[11px] text-gray-400">No matches.</div>
                )}
                {catFiltered.slice(0, 100).map((c, i) => (
                  <button
                    key={`${c.category_id}-${i}`}
                    onClick={() => {
                      setSelectedCat({ category_id: c.category_id, category_name: c.category_name });
                      setChangingCat(false);
                      setCatQ("");
                    }}
                    className="w-full text-left px-3 py-1.5 hover:bg-[#0020C2]/5
                      border-b border-gray-50 last:border-0 flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[11px] text-gray-800">{c.category_name}</span>
                    <span className="text-[10px] text-gray-400 font-mono shrink-0">#{c.category_id}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {phase === "category" && (
          <button
            onClick={confirmCategory}
            disabled={!selectedCat}
            className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
              uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
            Confirm category & find item
          </button>
        )}

        {/* ORG picker - only when the BU has multiple inventory organizations */}
        {phase === "org" && (
          <div className="space-y-2 pt-1 border-t border-gray-100">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 pt-1">
              Inventory organization
            </div>
            <div className="text-[11px] text-gray-500">
              This business unit has multiple inventory organizations. Select the
              one to source items from.
            </div>
            <div className="border border-[#0020C2]/20 rounded-lg overflow-hidden">
              <div className="max-h-44 overflow-y-auto">
                {orgOptions.length === 0 && (
                  <div className="px-3 py-2 text-[11px] text-gray-400">No organizations found.</div>
                )}
                {orgOptions.map((o, i) => (
                  <button
                    key={`${o.organization_id}-${i}`}
                    onClick={() => pickOrg(o)}
                    className="w-full text-left px-3 py-2 hover:bg-[#0020C2]/5
                      border-b border-gray-50 last:border-0">
                    <span className="text-xs font-semibold text-gray-800">
                      {o.organization_name || o.organization_code || `Org ${o.organization_id}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ITEM block - shown once an org is resolved (item/done phases) */}
        {(phase === "item" || phase === "done") && (
          <div className="space-y-2 pt-1 border-t border-gray-100">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 pt-1">Item</div>

            {itemLoading && (
              <div className="px-1 py-2 text-xs text-gray-400 flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                Finding the best item for "{data.item}"...
              </div>
            )}

            {itemErr && <div className="text-[11px] text-red-600">{itemErr}</div>}

            {!itemLoading && !itemErr && (
              <>
                {selectedItem ? (
                  <div className="rounded-lg border border-[#0020C2]/20 bg-[#0020C2]/5 px-3 py-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-gray-900 truncate">
                          {selectedItem.item_number || selectedItem.description || `Item ${selectedItem.item_id}`}
                        </div>
                        <div className="text-[10px] text-gray-500 font-mono">id {selectedItem.item_id}</div>
                        {selectedItem.description && selectedItem.item_number && (
                          <div className="text-[11px] text-gray-500 mt-0.5 truncate">{selectedItem.description}</div>
                        )}
                      </div>
                      {phase !== "done" && (
                        <button
                          onClick={() => setChangingItem((v) => !v)}
                          className="shrink-0 text-[10px] font-bold uppercase tracking-wider
                            text-[#0020C2] hover:underline flex items-center gap-0.5">
                          Change <CaretDown size={10} weight="bold" className={changingItem ? "rotate-180" : ""} />
                        </button>
                      )}
                    </div>
                    {itemMatched && selectedItem.item_id === itemMatched.item_id && itemMatched.reason && (
                      <div className="text-[11px] text-gray-500 mt-1">{itemMatched.reason}</div>
                    )}
                    {itemMatched && selectedItem.item_id === itemMatched.item_id && itemMatched.confidence != null && (
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        confidence {Math.round(itemMatched.confidence * 100)}%
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    No specific item matched - the line will use the category only, or pick an item below.
                  </div>
                )}

                {phase !== "done" && (changingItem || !selectedItem) && (
                  <div className="border border-[#0020C2]/20 rounded-lg overflow-hidden">
                    <input
                      autoFocus
                      value={itemQ}
                      onChange={(e) => setItemQ(e.target.value)}
                      placeholder="Search items in this category..."
                      className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
                    />
                    <div className="max-h-40 overflow-y-auto">
                      {itemLov.length === 0 && (
                        <div className="px-3 py-2 text-[11px] text-gray-400">
                          No items found for this category - the line will be category-only.
                        </div>
                      )}
                      {itemLov.length > 0 && itemFiltered.length === 0 && (
                        <div className="px-3 py-2 text-[11px] text-gray-400">No matches.</div>
                      )}
                      {itemFiltered.slice(0, 100).map((it, i) => (
                        <button
                          key={`${it.item_id}-${i}`}
                          onClick={() => {
                            setSelectedItem({
                              item_id: it.item_id,
                              item_number: it.item_number,
                              description: it.description,
                            });
                            setChangingItem(false);
                            setItemQ("");
                          }}
                          className="w-full text-left px-3 py-1.5 hover:bg-[#0020C2]/5
                            border-b border-gray-50 last:border-0">
                          <div className="text-[11px] text-gray-800 truncate">
                            {it.item_number || it.description || `Item ${it.item_id}`}
                          </div>
                          {it.description && it.item_number && (
                            <div className="text-[10px] text-gray-400 truncate">{it.description}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {phase === "item" && (
                  <div className="space-y-1.5">
                    <button
                      onClick={() => confirmAll(false)}
                      disabled={!selectedItem}
                      className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
                        uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
                      Confirm category & item
                    </button>
                    <button
                      onClick={() => confirmAll(true)}
                      className="w-full border border-gray-300 text-gray-600 py-2 rounded-lg
                        text-xs font-bold uppercase tracking-wider hover:bg-gray-50">
                      Skip item — category-only line
                    </button>
                    <div className="text-[10px] text-gray-400 text-center">
                      A category-only line uses your description "{data.item}" and no specific Oracle item.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {phase === "done" && (
          <div className="text-[11px] text-emerald-700 font-semibold text-center py-1">
            {"\u2713"} {confirmedWithItem ? "Category & item confirmed" : "Category confirmed (category-only line)"}
          </div>
        )}
      </div>
    </div>
  );
}

/* Supplier row (with "why?" toggle) */
function SupplierRow({ s, badge, selected, onToggle }) {
  const idOf = (x) => x.supplier_id ?? x.supplier_number ?? x.id ?? x.number;
  const nameOf = (x) => x.supplier_name ?? x.name ?? "Unknown supplier";
  const k = String(idOf(s));
  const isSel = !!selected[k];
  const [open, setOpen] = React.useState(false);

  const why = s.why;
  const products = s.products || [];
  const cats = s.categories || [];
  const hasDetail = why || products.length > 0 || cats.length > 0 || s.purchase_count != null;

  return (
    <div className={`rounded-lg mb-1 border ${isSel ? "border-[#0020C2] bg-[#0020C2]/5" : "border-transparent"}`}>
      <div className="flex items-center gap-2 px-2.5 py-2">
        <button onClick={() => onToggle(s)}
          className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center
            ${isSel ? "bg-[#0020C2] border-[#0020C2]" : "border-gray-300"}`}>
          {isSel && <span className="text-white text-[10px] leading-none">{"\u2713"}</span>}
        </button>
        <button onClick={() => onToggle(s)} className="min-w-0 flex-1 text-left">
          <span className="block text-xs font-semibold text-gray-800 truncate">{nameOf(s)}</span>
          {idOf(s) != null && (
            <span className="block text-[10px] text-gray-400 font-mono">#{idOf(s)}</span>
          )}
        </button>
        {badge != null && (
          <span className="shrink-0 text-[10px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">
            {badge}
          </span>
        )}
        {hasDetail && (
          <button onClick={() => setOpen((v) => !v)}
            className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-[#0020C2]
              hover:underline flex items-center gap-0.5">
            why? <CaretDown size={9} weight="bold" className={open ? "rotate-180" : ""} />
          </button>
        )}
      </div>

      {open && (
        <div className="px-3 pb-2 -mt-0.5 space-y-1.5">
          {why && (
            <div className="text-[11px] text-gray-700 bg-white border border-gray-100 rounded p-2">
              {why}
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
            {s.purchase_count != null && <div>Purchases: <span className="text-gray-800 font-semibold">{s.purchase_count}</span></div>}
            {s.total_amount != null && <div>Total spend: <span className="text-gray-800 font-semibold">{Number(s.total_amount).toFixed(2)}</span></div>}
            {s.total_quantity != null && <div>Total qty: <span className="text-gray-800 font-semibold">{s.total_quantity}</span></div>}
            {s.last_purchase_date && <div>Last: <span className="text-gray-800 font-semibold">{String(s.last_purchase_date).slice(0, 10)}</span></div>}
          </div>
          {cats.length > 0 && (
            <div className="text-[10px] text-gray-500">
              Categories: <span className="text-gray-700">{cats.slice(0, 4).join(", ")}</span>
            </div>
          )}
          {products.length > 0 && (
            <div className="text-[10px] text-gray-500">
              Supplied: <span className="text-gray-700">{products.slice(0, 5).join(", ")}{products.length > 5 ? "..." : ""}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Supplier suggestion (ranked + why? + full LOV) */
function SupplierSuggestion({ data, onConfirm }) {
  const categoryId = data?.category_id ?? null;
  const categoryName = data?.category_name || "";

  const [ranked, setRanked] = React.useState([]);
  const [allSuppliers, setAllSuppliers] = React.useState([]);
  const [selected, setSelected] = React.useState({});
  const [loadingRanked, setLoadingRanked] = React.useState(true);
  const [loadingAll, setLoadingAll] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [done, setDone] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [showAll, setShowAll] = React.useState(false);

  const idOf = (s) => s.supplier_id ?? s.supplier_number ?? s.id ?? s.number;
  const nameOf = (s) => s.supplier_name ?? s.name ?? "Unknown supplier";
  const scoreOf = (s) => s.score ?? s.count ?? s.po_count ?? null;
  const pcOf = (s) => s.purchase_count ?? null;

  React.useEffect(() => {
    if (categoryId == null) { setErr("No category id - confirm a category first."); setLoadingRanked(false); return; }
    let alive = true;
    setLoadingRanked(true);
    api.post("/erp-agents/rfp/supplier-suggestions", {
      category_id: categoryId,
      category_name: categoryName,
    })
      .then(({ data: res }) => {
        if (!alive) return;
        const items = Array.isArray(res) ? res : (res?.items || res?.suppliers || []);
        setRanked(items);
      })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || e.message || "Could not load suppliers."); })
      .finally(() => { if (alive) setLoadingRanked(false); });
    return () => { alive = false; };
  }, [categoryId, categoryName]);

  React.useEffect(() => {
    let alive = true;
    setLoadingAll(true);
    api.get("/erp-agents/rfp/suppliers/all")
      .then(({ data: res }) => {
        if (!alive) return;
        const items = Array.isArray(res) ? res : (res?.items || res?.suppliers || []);
        setAllSuppliers(items);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoadingAll(false); });
    return () => { alive = false; };
  }, []);

  const toggle = (s) => {
    if (done) return;
    setSelected((prev) => {
      const k = String(idOf(s));
      const next = { ...prev };
      if (next[k]) delete next[k]; else next[k] = s;
      return next;
    });
  };

  const rankedIds = new Set(ranked.map((s) => String(idOf(s))));
  const allFiltered = allSuppliers
    .filter((s) => !rankedIds.has(String(idOf(s))))
    .filter((s) => !q || nameOf(s).toLowerCase().includes(q.toLowerCase()));

  const chosen = Object.values(selected);
  const canConfirm = chosen.length > 0;

  const confirm = () => {
    if (!canConfirm) return;
    setDone(true);
    const list = chosen.map((s) => ({
      supplier_name: nameOf(s),
      supplier_id: idOf(s),
      supplier_number: idOf(s),
    }));
    const names = list.map((s) => s.supplier_name).join(", ");
    onConfirm?.({
      suppliers: list,
      text: `Selected supplier${list.length > 1 ? "s" : ""}: ${names}.`,
    });
  };

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid="rfp-supplier-suggestion">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          Suggested suppliers
        </span>
        {categoryName && (
          <span className="text-[10px] text-gray-400 normal-case tracking-normal truncate">
            {"\u00b7 "}{categoryName}
          </span>
        )}
      </div>

      {!done && (
        <>
          <div className="px-2 pt-2">
            <div className="px-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
              Top by purchase history
            </div>
            {err && <div className="px-2 py-2 text-xs text-red-600">{err}</div>}
            {!err && loadingRanked && (
              <div className="px-2 py-3 text-xs text-gray-400 flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                Ranking suppliers & explaining fit...
              </div>
            )}
            {!err && !loadingRanked && ranked.length === 0 && (
              <div className="px-2 py-2 text-[11px] text-gray-400">
                No ranked suppliers for this category - pick from the full list below.
              </div>
            )}
            {!loadingRanked && ranked.map((s, i) => (
              <SupplierRow
                key={`r-${idOf(s)}-${i}`}
                s={s}
                badge={scoreOf(s) != null ? `${scoreOf(s)} POs` : null}
                selected={selected}
                onToggle={toggle}
              />
            ))}
          </div>

          <div className="px-2 pt-1 pb-2 border-t border-gray-100 mt-1">
            <button
              onClick={() => setShowAll((v) => !v)}
              className="w-full flex items-center justify-between px-1 py-1.5 text-[10px]
                font-bold uppercase tracking-wider text-[#0020C2] hover:underline">
              <span>Browse all suppliers</span>
              <CaretDown size={11} weight="bold" className={showAll ? "rotate-180" : ""} />
            </button>

            {showAll && (
              <div className="mt-1">
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search all suppliers..."
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg
                    focus:outline-none focus:border-[#0020C2] mb-1"
                />
                <div className="max-h-48 overflow-y-auto">
                  {loadingAll && (
                    <div className="px-2 py-3 text-xs text-gray-400 flex items-center gap-2">
                      <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                      Loading suppliers...
                    </div>
                  )}
                  {!loadingAll && allFiltered.length === 0 && (
                    <div className="px-2 py-2 text-[11px] text-gray-400">
                      {allSuppliers.length === 0 ? "No supplier list available." : "No matches."}
                    </div>
                  )}
                  {!loadingAll && allFiltered.slice(0, 100).map((s, i) => (
                    <SupplierRow
                      key={`a-${idOf(s)}-${i}`}
                      s={s}
                      badge={pcOf(s) != null ? `${pcOf(s)} POs` : null}
                      selected={selected}
                      onToggle={toggle}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-100">
            <div className="mb-2 text-[10px] text-gray-400">
              {chosen.length} selected{chosen.length === 0 ? " \u00b7 pick at least one" : ""}
            </div>
            <button
              onClick={confirm}
              disabled={!canConfirm}
              className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
                uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
              Confirm supplier{chosen.length > 1 ? "s" : ""}
            </button>
          </div>
        </>
      )}

      {done && (
        <div className="p-3">
          <div className="text-[11px] text-emerald-700 font-semibold mb-1">{"\u2713"} Suppliers confirmed</div>
          <div className="flex flex-wrap gap-1">
            {chosen.map((s, i) => (
              <span key={i} className="text-[10px] bg-[#0020C2]/10 text-[#0020C2] rounded px-1.5 py-0.5">
                {nameOf(s)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* PR (Requisition) summary — editable per-line, then feeds the category step.
   Rendered from a parsed requisition. Each line's item/category/qty/price are
   editable; on confirm, the first line is pushed into the existing category ->
   supplier flow (multi-line: the rest are queued via onQueueLines). */
function PRRequisitionSummary({ data, onConfirmLine }) {
  const [currency, setCurrency] = React.useState(data?.currency || "AED");
  const [lines, setLines] = React.useState(
    (data?.lines || []).map((l) => ({
      item: l.item || l.description || "",
      item_code: l.item_code || "",
      category: l.category || "",
      quantity: l.quantity != null ? String(l.quantity) : "",
      unit_price: l.unit_price != null ? String(l.unit_price) : "",
    }))
  );
  const [done, setDone] = React.useState(false);

  const warnings = data?.warnings || [];
  const prNumber = data?.pr_number;
  const buName = data?.procurement_bu;
  const buAuthorized = data?.bu_authorized;

  const setLine = (i, key, val) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [key]: val } : l)));

  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const lineValid = (l) => l.item.trim() && parseFloat(l.quantity) > 0;
  const allValid = lines.length > 0 && lines.every(lineValid) && buAuthorized;

  const confirm = () => {
    if (!allValid) return;
    setDone(true);
    const payloadLines = lines.map((l) => ({
      item: l.item.trim(),
      item_code: l.item_code || null,
      category: l.category || null,
      quantity: parseFloat(l.quantity) || 0,
      unit_price: parseFloat(l.unit_price) || 0,
      amount: (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0),
    }));
    onConfirmLine?.({
      pr_number: prNumber,
      procurement_bu: buName,
      procurement_bu_id: data?.procurement_bu_id,
      currency,
      lines: payloadLines,
    });
  };

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid="rfp-pr-summary">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          Requisition {prNumber ? `\u00b7 ${prNumber}` : ""}
        </span>
      </div>

      <div className="p-3 space-y-3">
        {/* BU + currency header */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Procurement BU</label>
            <div className={`mt-0.5 text-xs px-2 py-1.5 rounded-lg border ${
              buAuthorized ? "border-gray-200 bg-gray-50 text-gray-800"
                           : "border-rose-200 bg-rose-50/40 text-rose-700"}`}>
              {buName || "\u2014"}
              {!buAuthorized && (
                <div className="text-[10px] mt-0.5">Not authorized — select an authorized BU in the wizard on the right.</div>
              )}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Currency</label>
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              className="mt-0.5 w-full text-xs px-2 py-1.5 border border-gray-300 rounded-lg
                focus:outline-none focus:border-[#0020C2] font-mono"
            />
          </div>
        </div>

        {warnings.length > 0 && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 space-y-0.5">
            {warnings.map((w, i) => <div key={i}>{w}</div>)}
          </div>
        )}

        {/* Lines */}
        <div className="space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Lines ({lines.length})
          </div>
          {lines.length === 0 && (
            <div className="text-[11px] text-gray-400">No lines parsed from the requisition.</div>
          )}
          {lines.map((l, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-2 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-gray-400">
                  Line {i + 1}{l.item_code ? ` \u00b7 ${l.item_code}` : ""}
                </span>
                {!done && lines.length > 1 && (
                  <button onClick={() => removeLine(i)} className="text-gray-300 hover:text-rose-500">
                    <Trash size={11} weight="bold" />
                  </button>
                )}
              </div>
              <input
                value={l.item}
                onChange={(e) => setLine(i, "item", e.target.value)}
                disabled={done}
                placeholder="Item / description"
                className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-lg
                  focus:outline-none focus:border-[#0020C2] disabled:bg-gray-50"
              />
              <div className="grid grid-cols-3 gap-1.5">
                <input
                  value={l.category}
                  onChange={(e) => setLine(i, "category", e.target.value)}
                  disabled={done}
                  placeholder="Category"
                  className="text-xs px-2 py-1.5 border border-gray-300 rounded-lg
                    focus:outline-none focus:border-[#0020C2] disabled:bg-gray-50"
                />
                <input
                  value={l.quantity}
                  onChange={(e) => setLine(i, "quantity", e.target.value)}
                  disabled={done}
                  inputMode="decimal"
                  placeholder="Qty"
                  className="text-xs px-2 py-1.5 border border-gray-300 rounded-lg text-right
                    focus:outline-none focus:border-[#0020C2] disabled:bg-gray-50"
                />
                <input
                  value={l.unit_price}
                  onChange={(e) => setLine(i, "unit_price", e.target.value)}
                  disabled={done}
                  inputMode="decimal"
                  placeholder="Unit price"
                  className="text-xs px-2 py-1.5 border border-gray-300 rounded-lg text-right
                    focus:outline-none focus:border-[#0020C2] disabled:bg-gray-50"
                />
              </div>
              {parseFloat(l.quantity) > 0 && parseFloat(l.unit_price) > 0 && (
                <div className="text-[10px] text-gray-500 text-right">
                  Line total: <span className="font-mono font-semibold text-gray-800">
                    {currency} {(parseFloat(l.quantity) * parseFloat(l.unit_price)).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {!done ? (
          <button
            onClick={confirm}
            disabled={!allValid}
            className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
              uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
            Confirm requisition & continue
          </button>
        ) : (
          <div className="text-[11px] text-emerald-700 font-semibold text-center py-1">
            {"\u2713"} Requisition confirmed — proceeding to category & suppliers
          </div>
        )}
      </div>
    </div>
  );
}

/* Collaboration team-member picker (after suppliers, before create).
   Fetches procurement agents scoped to the BU, lets the user pick members and
   set each one's access level (Full / View Only). Optional — can be skipped. */
function CollabTeamPicker({ data, onConfirm }) {
  const buId = data?.procurement_bu_id ?? null;
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [selected, setSelected] = React.useState({});  // key -> {member, access_code}
  const [q, setQ] = React.useState("");
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    if (buId == null) { setErr("No Procurement BU id."); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    api.get("/erp-agents/rfp/team-members", { params: { procurement_bu_id: buId } })
      .then(({ data: res }) => { if (alive) setMembers(res?.members || []); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || e.message || "Could not load team members."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [buId]);

  const keyOf = (m) => String(m.person_id ?? m.name);

  const toggle = (m) => {
    if (done) return;
    setSelected((prev) => {
      const k = keyOf(m);
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = { member: m, access_code: "FULL" };
      return next;
    });
  };

  const setAccess = (k, code) =>
    setSelected((prev) => (prev[k] ? { ...prev, [k]: { ...prev[k], access_code: code } } : prev));

  const filtered = q
    ? members.filter((m) => (m.name || "").toLowerCase().includes(q.toLowerCase()))
    : members;

  const chosen = Object.entries(selected);

  const finish = (skip) => {
    setDone(true);
    const list = skip ? [] : chosen.map(([, v]) => ({
      name: v.member.name,
      person_id: v.member.person_id,
      access_code: v.access_code,
      access: v.access_code === "FULL" ? "Full" : "View Only",
      price_visibility: v.access_code === "FULL",
    }));
    const text = list.length
      ? `Collaboration team: ${list.map((m) => `${m.name} (${m.access})`).join(", ")}.`
      : "No collaboration team members added.";
    onConfirm?.({ collaboration_members: list, text });
  };

  return (
    <div className="mt-2 border border-[#0020C2]/25 rounded-xl bg-white overflow-hidden shadow-sm"
      data-testid="rfp-collab-picker">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-[#0020C2]/5">
        <Sparkle size={11} weight="fill" className="text-[#0020C2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">
          Collaboration team members
        </span>
        <span className="text-[10px] text-gray-400 normal-case tracking-normal">{"\u00b7"} optional</span>
      </div>

      {!done ? (
        <>
          <div className="px-2 pt-2">
            {err && <div className="px-2 py-2 text-xs text-red-600">{err}</div>}
            {!err && loading && (
              <div className="px-2 py-3 text-xs text-gray-400 flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                Loading team members...
              </div>
            )}
            {!err && !loading && (
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search people..."
                className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg
                  focus:outline-none focus:border-[#0020C2] mb-1"
              />
            )}
            {!loading && !err && (
              <div className="max-h-52 overflow-y-auto">
                {filtered.length === 0 && (
                  <div className="px-2 py-2 text-[11px] text-gray-400">
                    {members.length === 0 ? "No team members available for this BU." : "No matches."}
                  </div>
                )}
                {filtered.map((m, i) => {
                  const k = keyOf(m);
                  const sel = selected[k];
                  return (
                    <div key={`${k}-${i}`}
                      className={`rounded-lg mb-1 border ${sel ? "border-[#0020C2] bg-[#0020C2]/5" : "border-transparent"}`}>
                      <div className="flex items-center gap-2 px-2.5 py-2">
                        <button onClick={() => toggle(m)}
                          className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center
                            ${sel ? "bg-[#0020C2] border-[#0020C2]" : "border-gray-300"}`}>
                          {sel && <span className="text-white text-[10px] leading-none">{"\u2713"}</span>}
                        </button>
                        <button onClick={() => toggle(m)} className="min-w-0 flex-1 text-left">
                          <span className="block text-xs font-semibold text-gray-800 truncate">{m.name}</span>
                          {m.person_id != null && (
                            <span className="block text-[10px] text-gray-400 font-mono">#{m.person_id}</span>
                          )}
                        </button>
                        {sel && (
                          <select
                            value={sel.access_code}
                            onChange={(e) => setAccess(k, e.target.value)}
                            className="shrink-0 text-[11px] border border-gray-300 rounded-lg px-1.5 py-1
                              bg-white focus:outline-none focus:border-[#0020C2] cursor-pointer"
                            title="Access level">
                            <option value="FULL">Full</option>
                            <option value="VIEW_ONLY">View Only</option>
                          </select>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-gray-100 space-y-1.5">
            <div className="text-[10px] text-gray-400">{chosen.length} selected</div>
            <button
              onClick={() => finish(false)}
              disabled={chosen.length === 0}
              className="w-full bg-[#0020C2] text-white py-2 rounded-lg text-xs font-bold
                uppercase tracking-wider hover:bg-[#001AA3] disabled:opacity-40">
              Add team member{chosen.length > 1 ? "s" : ""} & continue
            </button>
            <button
              onClick={() => finish(true)}
              className="w-full border border-gray-300 text-gray-600 py-2 rounded-lg
                text-xs font-bold uppercase tracking-wider hover:bg-gray-50">
              Skip — no team members
            </button>
          </div>
        </>
      ) : (
        <div className="p-3">
          <div className="text-[11px] text-emerald-700 font-semibold mb-1">
            {"\u2713"} {chosen.length ? "Team members added" : "No team members added"}
          </div>
          {chosen.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {chosen.map(([k, v], i) => (
                <span key={i} className="text-[10px] bg-[#0020C2]/10 text-[#0020C2] rounded px-1.5 py-0.5">
                  {v.member.name} ({v.access_code === "FULL" ? "Full" : "View Only"})
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Chat bubble */
function ChatBubble({ msg, messages, onOptionClick, onMultiSubmit, onLineResolved, onCategoryConfirm, onSupplierConfirm, onPRConfirm, onCollabConfirm }) {
  const isUser = msg.role === "user";
  const fieldUi = !isUser && !msg.loading ? msg.result?.next_field_ui : null;

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {!isUser && (
        <div className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white bg-[#0020C2]">
          <Sparkle size={11} weight="fill" />
        </div>
      )}

      <div className={`text-sm leading-relaxed
        ${isUser
          ? "max-w-[82%] rounded-2xl px-3.5 py-2 bg-[#0020C2] text-white rounded-tr-sm"
          : "flex-1 min-w-0 text-gray-900 pt-0.5"}`}>

        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {!msg.loading && msg.text && (
          isUser ? <p className="whitespace-pre-wrap">{msg.text}</p> : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {(fieldUi?.uiType === "amount_input" || fieldUi?.uiType === "line_items_builder") && (
          <RFPLineInput
            fieldUi={fieldUi}
            messages={messages}
            done={!!msg.line_done}
            onResolved={(payload) => onLineResolved(msg.id, payload)}
          />
        )}

        {msg.pr_data && (
          <PRRequisitionSummary data={msg.pr_data} onConfirmLine={(payload) => onPRConfirm(msg.id, payload)} />
        )}

        {msg.category_data && (
          <CategorySuggestion data={msg.category_data} onConfirm={onCategoryConfirm} />
        )}

        {msg.supplier_data && (
          <SupplierSuggestion
            data={msg.supplier_data}
            onConfirm={(payload) => onSupplierConfirm(msg.id, payload)}
          />
        )}

        {msg.collab_data && (
          <CollabTeamPicker
            data={msg.collab_data}
            onConfirm={(payload) => onCollabConfirm(msg.id, payload)}
          />
        )}

        {!["buyer_lov", "amount_input", "line_items_builder"].includes(fieldUi?.uiType) &&
          fieldUi?.options?.length > 0 && (
          <div
            className={fieldUi.uiType === "dropdown" ? "mt-2 flex flex-col gap-1" : "mt-2 flex flex-wrap gap-1.5"}
            data-testid={`chat-field-ui-${fieldUi.field}`}
          >
            {fieldUi.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onOptionClick?.(opt)}
                className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left"
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

/* Input bar */
function InputBar({ inputRef, value, onChange, onKeyDown, onSend, busy, placeholder }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50
        focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder={placeholder}
          data-testid="agent-prompt"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none
            outline-none placeholder-gray-400 font-mono disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center
            hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">{"\u21b5"} send {"\u00b7"} shift+{"\u21b5"} new line</span>
    </div>
  );
}

/* Chat history control */
function ChatHistoryControl({ agentKey, currentSessionId, onNewChat, onOpenSession }) {
  const [open, setOpen] = React.useState(false);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!agentKey) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/erp-agents/${agentKey}/sessions`);
      setSessions(Array.isArray(data) ? data : []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [agentKey]);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/erp-agents/${agentKey}/sessions/${sid}`);
      load();
    } catch {}
  };

  return (
    <div className="flex items-center gap-3" ref={wrapRef}>
      <button
        onClick={onNewChat}
        className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
        data-testid="chat-new"
        title="Start a new conversation">
        <Plus size={13} weight="bold" /> New Chat
      </button>

      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1"
          data-testid="chat-history-toggle"
          title="Chat history">
          History
          <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute right-0 z-40 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg
            max-h-80 overflow-y-auto" data-testid="chat-history-list">
            {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading...</div>}
            {!loading && sessions.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-400">No previous conversations.</div>
            )}
            {sessions.map((s) => (
              <div
                key={s.session_id}
                onClick={() => { onOpenSession(s.session_id); setOpen(false); }}
                className={`px-3 py-2 text-xs cursor-pointer border-b border-gray-50 last:border-0
                  hover:bg-[#0020C2]/5 flex items-center gap-2
                  ${s.session_id === currentSessionId ? "bg-[#0020C2]/5" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 truncate">{s.title}</div>
                  <div className="text-[10px] text-gray-400 flex items-center gap-1.5">
                    <span className={`px-1 py-0.5 rounded text-[8px] font-bold uppercase
                      ${s.status === "completed" ? "bg-emerald-100 text-emerald-700"
                        : s.status === "cancelled" ? "bg-gray-200 text-gray-500"
                        : "bg-blue-100 text-blue-700"}`}>
                      {s.status}
                    </span>
                    {s.updated_at ? new Date(s.updated_at).toLocaleString([], {
                      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                    }) : ""}
                  </div>
                </div>
                <button
                  onClick={(e) => deleteSession(s.session_id, e)}
                  className="text-[#DC2626] shrink-0 p-1" title="Delete">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* Main RFP modal */
export default function RFPAgentModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents, launchAgent } = useErp();

  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [chatStarted, setChatStarted] = React.useState(false);
  const [prMode, setPrMode] = React.useState(false);  // true once a PR is uploaded → wizard hidden, no re-upload
  const [showDashboard, setShowDashboard] = React.useState(false);
  const [responseVisibility, setResponseVisibility] = React.useState("Open");  // PDF-flow toggle
  const [sessionId, setSessionId] = React.useState(() => crypto.randomUUID());

  const [fieldValues, setFieldValues] = React.useState({});
  const [missingFields, setMissingFields] = React.useState([]);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [fieldWarnings, setFieldWarnings] = React.useState({});
  const [wizardSchemaFields, setWizardSchemaFields] = React.useState([]);

  const agent = agents.find((a) => a.key === openAgent);
  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const messagesRef = React.useRef([]);
  React.useEffect(() => { messagesRef.current = messages; }, [messages]);

  const ts = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // Push the PDF-flow response-visibility choice into fieldValues so it's sent
  // with the create. Only relevant in prMode; chat flow uses the wizard value.
  React.useEffect(() => {
    if (prMode) {
      setFieldValues((prev) => ({ ...prev, response_visibility: responseVisibility }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseVisibility, prMode]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  React.useEffect(() => {
    setMessages([]);
    setInput("");
    setBusy(false);
    setChatStarted(false);
    setFieldValues({});
    setMissingFields([]);
    setFieldErrors({});
    setFieldWarnings({});
    setWizardSchemaFields([]);
    setPrMode(false);
    setResponseVisibility("Open");
    setSessionId(crypto.randomUUID());
  }, [openAgent]);

  const close = () => {
    setOpenAgent(null);
    setMessages([]);
    setFieldValues({});
    setChatStarted(false);
  };

  const payloadFV = () => {
    const out = {};
    for (const [k, v] of Object.entries(fieldValues)) {
      if (k.endsWith("__label")) continue;
      out[k] = v;
    }
    return out;
  };

  const sendMessage = async (text) => {
    const rawText = (text || "").trim();
    if (!rawText || busy) return;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const now = ts();
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", text: rawText, timestamp: now },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);
    setMissingFields([]);
    setFieldErrors({});
    setFieldWarnings({});

    try {
      const res = await api.post(CHAT_ENDPOINT, {
        session_id: sessionId,
        message: rawText,
        ...payloadFV(),
      });
      const data = res.data;

      const status = (data?.status || "").toLowerCase();
      const backendMsg = data?.reply || data?.response || data?.message;

      const reply =
        ["ok", "success", "completed"].includes(status)
          ? backendMsg || `Done! ${agent?.label || "RFP"} submitted to Oracle successfully.`
          : ["needs_input", "validating_fields"].includes(status)
            ? backendMsg || "Please fill in the required fields."
            : status === "awaiting_confirm"
              ? backendMsg || "Please review and confirm the details."
              : status === "llm_error"
                ? backendMsg || "I had trouble parsing that. Could you rephrase?"
                : status === "oracle_error"
                  ? backendMsg || `Oracle returned an error (HTTP ${data?.http_status}).`
                  : status === "unconfigured"
                    ? backendMsg || "Oracle profile isn't set up. Click the gear to configure it."
                    : backendMsg || "Something unexpected happened.";

      const now2 = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading ? { ...m, loading: false, text: reply, result: data, timestamp: now2 } : m
        )
      );

      // Two-step cover page: once the negotiation is created (id returned),
      // auto-generate + PUT the cover page. Best-effort — a cover-page failure
      // doesn't undo the created RFP; we just note it in chat.
      if (data?.negotiation_id) {
        const fv = { ...fieldValues };
        (async () => {
          try {
            const { data: cp } = await api.post("/erp-agents/rfp/cover-page", {
              negotiation_id: String(data.negotiation_id),
              title: fv.title || fv.item ? `Request for Proposal${fv.item ? ` - ${fv.item}` : ""}` : "Request for Proposal",
              procurement_bu: fv.procurement_bu || null,
              buyer: fv.buyer_name || null,
              currency: fv.currency || null,
              close_date: fv.close_date || null,
              // Minimal line/supplier context for the generated HTML.
              lines: fv.item ? [{ item: fv.item, quantity: fv.quantity, category: fv.category }] : [],
              suppliers: Array.isArray(fv.suppliers) ? fv.suppliers : [],
            });
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now() + 5,
                role: "assistant",
                timestamp: ts(),
                text: cp?.ok
                  ? "\u2713 Cover page added to the negotiation."
                  : `The RFP was created, but the cover page couldn't be set (HTTP ${cp?.http_status}). You can add it in Oracle.`,
              },
            ]);
          } catch (err) {
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now() + 5,
                role: "assistant",
                timestamp: ts(),
                text: "The RFP was created, but setting the cover page failed. You can add it in Oracle.",
              },
            ]);
          }
        })();
      }

      if (data?.field_values && typeof data.field_values === "object") {
        setFieldValues((prev) => ({ ...prev, ...data.field_values }));
      }
      if (data?.fields_collected && typeof data.fields_collected === "object") {
        setFieldValues((prev) => ({ ...prev, ...data.fields_collected }));
      }
      setMissingFields(Array.isArray(data?.missing) ? data.missing
        : Array.isArray(data?.missing_fields) ? data.missing_fields : []);
      setFieldErrors(data?.field_errors && typeof data.field_errors === "object" ? data.field_errors : {});
      setFieldWarnings(data?.field_warnings && typeof data.field_warnings === "object" ? data.field_warnings : {});
    } catch (e) {
      const d = e?.response?.data?.detail;
      let errText;
      if (Array.isArray(d)) {
        // FastAPI validation errors: [{loc, msg, ...}]
        errText = d.map((x) => {
          const field = Array.isArray(x?.loc) ? x.loc[x.loc.length - 1] : "";
          return field ? `${field}: ${x?.msg || "invalid"}` : (x?.msg || "invalid");
        }).join("; ");
      } else if (typeof d === "string") {
        errText = d;
      } else if (d && typeof d === "object") {
        errText = JSON.stringify(d);
      } else {
        errText = e?.message || "Request failed";
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, timestamp: ts(), text: `Error: ${errText}` }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Upload a Requisition (PR) PDF -> parse -> show editable summary widget.
  const prFileRef = React.useRef(null);
  const [prUploading, setPrUploading] = React.useState(false);

  const handlePRUpload = async (file) => {
    if (!file) return;
    // Hide the wizard immediately — a PR is now the source of truth.
    setPrMode(true);
    if (!chatStarted) setChatStarted(true);
    const now = ts();
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", text: `\ud83d\udcce Uploaded requisition: ${file.name}`, timestamp: now },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setPrUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/erp-agents/rfp/parse-requisition", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (!data?.ok) {
        setPrMode(false);  // parse failed — bring the wizard back
        setMessages((prev) => prev.map((m) =>
          m.loading ? { ...m, loading: false, timestamp: ts(),
            text: data?.error || "I couldn't read that requisition PDF." } : m));
        return;
      }

      // PR is now the source of truth — wizard stays hidden, block re-uploads.
      setPrMode(true);

      // Seed BU + currency into the fields so downstream steps have them.
      setFieldValues((prev) => ({
        ...prev,
        ...(data.procurement_bu ? { procurement_bu: data.procurement_bu } : {}),
        ...(data.procurement_bu_id != null ? { procurement_bu_id: data.procurement_bu_id } : {}),
        ...(data.currency ? { currency: data.currency } : {}),
        ...(data.pr_number ? { pr_number: data.pr_number } : {}),
      }));

      const lineCount = (data.lines || []).length;
      const askBits = [];
      if (!data.bu_authorized) askBits.push("the Business Unit isn't one you're authorized for");
      if ((data.missing || []).length) askBits.push("some line details need your confirmation");
      const ask = askBits.length
        ? ` I've flagged a few things below — ${askBits.join(" and ")}. Please review and edit before continuing.`
        : " Review the details below, edit anything if needed, then confirm.";

      setMessages((prev) => prev.map((m) =>
        m.loading ? {
          ...m, loading: false, timestamp: ts(),
          text: `I read requisition ${data.pr_number || ""} and found ${lineCount} line${lineCount === 1 ? "" : "s"}.${ask}`,
          pr_data: data,
        } : m));
    } catch (e) {
      setPrMode(false);  // upload errored — bring the wizard back
      setMessages((prev) => prev.map((m) =>
        m.loading ? { ...m, loading: false, timestamp: ts(),
          text: `Error parsing requisition: ${e?.response?.data?.detail || e.message}` } : m));
    } finally {
      setPrUploading(false);
      if (prFileRef.current) prFileRef.current.value = "";
    }
  };

  // After the user confirms the parsed requisition, drive the FIRST line into
  // the existing category -> item -> supplier pipeline. Remaining lines are
  // queued and processed one-by-one after each supplier confirmation.
  const [prQueue, setPrQueue] = React.useState([]);

  const pushLineToCategory = async (line, buId, currency) => {
    setFieldValues((prev) => ({
      ...prev,
      item: line.item,
      quantity: line.quantity,
      ...(line.amount != null ? { amount: line.amount } : {}),
      ...(currency ? { currency } : {}),
    }));
    const loadingId = Date.now();
    setMessages((prev) => [
      ...prev,
      { id: loadingId, role: "assistant", loading: true },
    ]);
    try {
      const { data } = await api.post("/erp-agents/rfp/resolve-category", {
        procurement_bu_id: buId,
        item_text: line.item,
        with_suppliers: false,
        quantity: line.quantity ?? null,
        unit_price: line.unit_price || null,
        currency,
      });
      setMessages((prev) => prev.map((m) => m.id === loadingId ? {
        id: loadingId,
        role: "assistant",
        loading: false,
        timestamp: ts(),
        text: `Here's the best-matching category for ${line.quantity} \u00d7 ${line.item}. Confirm the category, then pick the item.`,
        category_data: {
          api: data,
          item: line.item,
          quantity: line.quantity,
          amount: line.amount ?? null,
          currency,
          procurement_bu_id: buId,
        },
      } : m));
    } catch (e) {
      setMessages((prev) => prev.map((m) => m.id === loadingId ? {
        id: loadingId, role: "assistant", loading: false, timestamp: ts(),
        text: `Category lookup failed for "${line.item}": ${e?.response?.data?.detail || e.message}`,
      } : m));
    }
  };

  const handlePRConfirm = (sourceMsgId, { pr_number, procurement_bu, procurement_bu_id, currency, lines }) => {
    setMessages((prev) => prev.map((m) => m.id === sourceMsgId ? { ...m, pr_confirmed: true } : m));

    const buId = procurement_bu_id ?? fieldValues.procurement_bu_id ?? null;
    setFieldValues((prev) => ({
      ...prev,
      ...(procurement_bu ? { procurement_bu } : {}),
      ...(procurement_bu_id != null ? { procurement_bu_id } : {}),
      ...(currency ? { currency } : {}),
    }));

    const [first, ...rest] = lines || [];
    setPrQueue(rest.map((l) => ({ ...l, buId, currency })));
    if (first) pushLineToCategory(first, buId, currency);
  };

  const handleLineResolved = (sourceMsgId, payload) => {
    setMessages((prev) => {
      const flagged = prev.map((m) =>
        m.id === sourceMsgId ? { ...m, line_done: true } : m
      );
      return [
        ...flagged,
        {
          id: Date.now(),
          role: "assistant",
          timestamp: ts(),
          text: `Here's the best-matching category for ${payload.quantity} \u00d7 ${payload.item}. Confirm the category, then pick the item.`,
          category_data: payload,
        },
      ];
    });
  };

  const handleCategoryConfirm = ({ item, quantity, amount, category_id, category_name, item_id, item_number, item_description, organization_id, currency, text }) => {
    setFieldValues((prev) => ({
      ...prev,
      item,
      quantity,
      ...(amount != null ? { amount } : {}),
      ...(currency ? { currency } : {}),
      ...(category_name ? { category: category_name } : {}),
      ...(category_id != null ? { category_id } : {}),
      ...(item_id != null ? { item_id } : {}),
      ...(item_number ? { item_number } : {}),
      ...(organization_id != null ? { organization_id } : {}),
      amount_step_done: true,
      item_step_done: true,
    }));

    if (category_id != null) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "assistant",
          timestamp: ts(),
          text: `Here are the suppliers most used for ${category_name || "this category"}, ranked by purchase history. Select at least one to invite.`,
          supplier_data: { category_id, category_name },
          category_summary: text,
        },
      ]);
    } else {
      sendMessage(text);
    }
  };

  const handleSupplierConfirm = (sourceMsgId, { suppliers, text }) => {
    setFieldValues((prev) => ({
      ...prev,
      suppliers,
      suppliers_step_done: true,
    }));

    const src = messagesRef.current.find((m) => m.id === sourceMsgId);
    const categorySummary = src?.category_summary || "";
    const combined = categorySummary ? `${categorySummary} ${text}` : text;

    // Instead of creating now, show the (optional) collaboration team-member
    // step. The final create happens after that step is confirmed/skipped.
    const buId = fieldValues.procurement_bu_id ?? null;
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        role: "assistant",
        timestamp: ts(),
        text: "Would you like to add collaboration team members to help manage this RFP? Pick people and their access, or skip.",
        collab_data: { procurement_bu_id: buId },
        pending_summary: combined,
      },
    ]);
  };

  const handleCollabConfirm = (sourceMsgId, { collaboration_members, text }) => {
    setFieldValues((prev) => ({
      ...prev,
      collaboration_members: collaboration_members || [],
      collaboration_step_done: true,
    }));

    const src = messagesRef.current.find((m) => m.id === sourceMsgId);
    const prior = src?.pending_summary || "";
    const combined = prior ? `${prior} ${text}` : text;
    sendMessage(combined);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const startNewChat = async () => {
    let newId = crypto.randomUUID();
    try {
      const { data } = await api.post(`/erp-agents/${AGENT_KEY}/sessions/new`);
      if (data?.session_id) newId = data.session_id;
    } catch {}
    setSessionId(newId);
    setMessages([]);
    setInput("");
    setChatStarted(false);
    setFieldValues({});
    setPrMode(false);
    setResponseVisibility("Open");
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/erp-agents/${AGENT_KEY}/sessions/${sid}/messages`);
      const loaded = (data?.messages || []).map((m, i) => ({
        id: Date.now() + i,
        role: m.role,
        text: m.content,
        timestamp: m.created_at
          ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : ts(),
        result: m.next_field_ui ? { next_field_ui: m.next_field_ui } : undefined,
      }));
      setSessionId(sid);
      setMessages(loaded);
      setChatStarted(true);
    } catch (e) {
      window.alert(`Could not open conversation: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (openAgent !== AGENT_KEY) return null;

  return (
    <>
      <style>{`
        @keyframes chatFadeIn    { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes wizardSlideIn { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        .rfp-modal-pre  { width:672px; max-width:calc(100vw - 2rem); max-height:90vh; }
        .rfp-modal-chat {
          width:min(74vw,1120px); height:78vh;
          resize: both; overflow: auto;
          min-width: 640px;  min-height: 460px;
          max-width: 96vw;   max-height: 94vh;
        }
        .rfp-modal-chat::-webkit-resizer { display: none; }
        .rfp-modal-resize-hint {
          position: absolute; right: 3px; bottom: 3px; width: 14px; height: 14px;
          cursor: nwse-resize; pointer-events: none; opacity: 0.5;
        }
      `}</style>

      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={() => { if (!chatStarted) close(); }}>
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={`agent-modal-${openAgent}`}
          className={`relative bg-white text-gray-900 border border-gray-300 flex flex-col
            ${(chatStarted || showDashboard) ? "rfp-modal-chat" : "rfp-modal-pre overflow-hidden"}`}
          style={{ transition: chatStarted ? "none" : "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {chatStarted && (
            <svg className="rfp-modal-resize-hint" viewBox="0 0 14 14" fill="none">
              <path d="M13 5 L5 13 M13 9 L9 13 M13 1 L1 13" stroke="#9CA3AF" strokeWidth="1.2" />
            </svg>
          )}

          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle Fusion ERP {"\u00b7"} Vaku AI
              </div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent?.label || "RFP"}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent?.hint}</p>

              {/* PDF flow only: response-visibility toggle (Open / Blind / Sealed).
                  Chat flow gets this from the wizard instead. */}
              {prMode && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    Response visibility
                  </span>
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                    {["Open", "Blind", "Sealed"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setResponseVisibility(opt)}
                        title={
                          opt === "Open" ? "Buyer sees responses as they arrive; suppliers can't see each other"
                          : opt === "Blind" ? "Buyer sees responses; suppliers can't see each other's bids"
                          : "Nobody sees bids until the negotiation is unsealed"
                        }
                        className={`text-[10px] font-bold px-2.5 py-1 rounded-md transition-colors
                          ${responseVisibility === opt
                            ? "bg-white text-[#0020C2] shadow-sm"
                            : "text-gray-500 hover:text-gray-700"}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {/* Chat / Manage RFP toggle (like the PO Create/Dashboard tabs) */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setShowDashboard(false)}
                  data-testid="rfp-tab-chat"
                  className={`flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-md transition-colors
                    ${!showDashboard ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                  <Sparkle size={12} weight="fill" /> Chat
                </button>
                <button
                  onClick={() => setShowDashboard(true)}
                  data-testid="rfp-tab-manage"
                  className={`flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-md transition-colors
                    ${showDashboard ? "bg-white text-[#0020C2] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                  <FileText size={12} weight="bold" /> Manage RFP
                </button>
              </div>

              {!showDashboard && chatStarted && (
                <>
                  <ChatHistoryControl
                    agentKey={AGENT_KEY}
                    currentSessionId={sessionId}
                    onNewChat={startNewChat}
                    onOpenSession={openSession}
                  />
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button
                onClick={() => { close(); setSetupOpen(true); }}
                data-testid="agent-open-profile"
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close} data-testid="agent-modal-close">
                <X size={18} />
              </button>
            </div>
          </div>

          {showDashboard ? (
            <div className="flex-1 overflow-y-auto">
              <RFPDashboard embedded onClose={() => setShowDashboard(false)} />
            </div>
          ) : !chatStarted ? (
            <>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <WizardFields
                  agentKey={openAgent}
                  values={fieldValues}
                  onChange={setFieldValues}
                  missingFields={missingFields}
                  onCreateNew={(key) => { close(); setTimeout(() => launchAgent(key), 100); }}
                  fieldErrors={fieldErrors}
                  fieldWarnings={fieldWarnings}
                  onSchemaLoad={setWizardSchemaFields}
                />
              </div>

              <div className="border-t border-gray-200 px-4 py-2 bg-white shrink-0">
                <div className="flex items-center justify-between gap-2 text-xs text-gray-400">
                  <div className="flex items-center gap-2">
                    <Sparkle size={12} weight="fill" />
                    <span>Type a message below to start the RFP chat</span>
                  </div>
                  <input
                    ref={prFileRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => handlePRUpload(e.target.files?.[0])}
                    data-testid="rfp-pr-upload-input"
                  />
                  <button
                    onClick={() => prFileRef.current?.click()}
                    disabled={prUploading || prMode || chatStarted}
                    className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold
                      uppercase tracking-wider text-[#0020C2] border border-[#0020C2]/30
                      rounded-lg px-2.5 py-1.5 hover:bg-[#0020C2]/5 disabled:opacity-40
                      disabled:cursor-not-allowed"
                    data-testid="rfp-pr-upload-btn"
                    title={prMode
                      ? "A requisition is already loaded"
                      : "Create RFP from an Oracle Requisition PDF"}>
                    <Paperclip size={12} weight="bold" />
                    {prUploading ? "Reading..." : "Create RFP from Requisition"}
                  </button>
                </div>
              </div>

              <InputBar
                inputRef={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onSend={() => sendMessage(input)}
                busy={busy}
                placeholder={agent?.hint || "Describe the RFP you want to create..."}
              />
            </>
          ) : (
            <div className="flex flex-1 overflow-hidden">
              <div
                className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{ width: prMode ? "100%" : "72%", animation: "chatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}>
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => (
                    <ChatBubble
                      key={msg.id}
                      msg={msg}
                      messages={messages}
                      onOptionClick={(opt) => sendMessage(opt.value)}
                      onMultiSubmit={(values) => sendMessage(values.join(", "))}
                      onLineResolved={handleLineResolved}
                      onCategoryConfirm={handleCategoryConfirm}
                      onSupplierConfirm={handleSupplierConfirm}
                      onPRConfirm={handlePRConfirm}
                      onCollabConfirm={handleCollabConfirm}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <InputBar
                  inputRef={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onSend={() => sendMessage(input)}
                  busy={busy}
                  placeholder="Continue the conversation..."
                />
              </div>

              {!prMode && (
              <div
                className="flex flex-col bg-white"
                style={{ width: "28%", minWidth: "300px", animation: "wizardSlideIn 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                    <Sparkle size={11} weight="fill" /> Wizard fields
                    <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
                      {"\u00b7"} auto-filled as you chat
                    </span>
                  </div>

                  <WizardFields
                    agentKey={openAgent}
                    values={fieldValues}
                    onChange={setFieldValues}
                    missingFields={missingFields}
                    onCreateNew={(key) => { close(); setTimeout(() => launchAgent(key), 100); }}
                    fieldErrors={fieldErrors}
                    fieldWarnings={fieldWarnings}
                    onSchemaLoad={setWizardSchemaFields}
                    stacked
                  />
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}